import React, { useCallback, useContext } from 'react';

import { isPlariumEnv } from 'App/constants';

import { ActionBar, Actions, Action, DownloadIcon } from '@gogame/components';
import { useChildComponent, useChooseFiles } from '@gogame/hooks-ts';
import { FullScreenToggler } from '@gogame/data-grid';
import { useParams, useScreenGo, useScreenState } from '@gogame/screens';

import ConceptsDataTableContext from 'scenes/Gallery/GalleryPage/Concepts/ConceptsDataTable/ConceptsDataTableContext';
import CreateConceptForm from 'scenes/Gallery/GalleryPage/Concepts/CreateConceptForm';
import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/constants';
import { useExportConcepts } from 'scenes/Gallery/GalleryPage/Concepts/data';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/constants';
import SearchField from 'scenes/Gallery/components/SearchField/SearchField';
import SwitchViewMode from 'scenes/Gallery/components/SwitchViewMode';
import {
  EXCEL,
  ZIP,
  VIEW_MODE_TILES,
  STATUS_PENDING,
  STATUS_ACTIVE,
  TAB_CONCEPTS,
} from 'scenes/Gallery/GalleryPage/constants';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import { getSoundId } from 'scenes/Gallery/GalleryPage/utils/getSoundId';

import OrderControl from './OrderControl';

const Toolbar = ({ tabs }) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const { viewMode, setViewMode, data, tableSettings } = useContext(
    ConceptsDataTableContext
  );
  const {
    isAllowCreateConcept,
    isAllowUploadPlayable,
    isAllowChangeConceptStatus,
    isAllowTested,
  } = useGalleryPermissions();
  const params = useParams();
  const { filters, searchQuery, searchType } = useScreenState();
  const go = useScreenGo();
  const chooseFiles = useChooseFiles({
    multiple: false,
    accept: `image/jpeg,image/jpg,image/png,image/gif,video/mp4,video/quicktime${
      isAllowUploadPlayable
        ? ',application/zip,application/x-zip-compressed,multipart/x-zip'
        : ''
    }`,
  });
  const [exportExcelConcepts] = useExportConcepts({
    type: EXCEL,
  });

  const [exportZipConcepts] = useExportConcepts({
    type: ZIP,
  });

  const handleExportExcelConcepts = useCallback(() => {
    return exportExcelConcepts({
      conceptFamilyId: params?.conceptFamilyId,
      tableSettings,
      filters,
      searchQuery,
      searchType,
    });
  }, [exportExcelConcepts]);

  const handleExportZipConcepts = useCallback(() => {
    const size = data.rows.reduce((result, { size = 0 }) => result + size, 0);

    return exportZipConcepts(
      {
        conceptFamilyId: params?.conceptFamilyId,
        tableSettings,
        filters,
        searchQuery,
        searchType,
      },
      { size }
    );
  }, [exportZipConcepts, data?.rows]);

  const handleExportZipConceptsFiltered = useCallback(() => {
    const size = data.rows.reduce((result, { size = 0 }) => result + size, 0);

    return exportZipConcepts(
      {
        conceptFamilyId: params?.conceptFamilyId,
        useInnerFilter: true,
        tableSettings,
        filters,
        searchQuery,
        searchType,
      },
      { size }
    );
  }, [exportZipConcepts, data?.rows]);

  const handleAddConcept = async () => {
    const file = await chooseFiles();
    const soundId = getSoundId(file?.name);
    const initialValues = {
      [CONCEPT.DESCRIPTION]: `Original File Name: ${file?.name}`,
      [CONCEPT.ORIGINAL]: {
        ...(isAllowTested
          ? {
              [CREATIVE.TESTED]: 0,
            }
          : null),
      },
      [CONCEPT.STATE_SCHEDULES]: [
        {
          state:
            isPlariumEnv && isAllowChangeConceptStatus
              ? STATUS_PENDING
              : STATUS_ACTIVE,
        },
      ],
    };

    if (soundId) {
      initialValues[CONCEPT.ORIGINAL][CREATIVE.SOUND_ID] = soundId;
    }

    openChildComponent(CreateConceptForm, {
      file,
      initialValues,
      onSubmit: ({ id }) => {
        go(`${TAB_CONCEPTS}/${id}`);
      },
    });
  };

  return (
    <>
      <ActionBar
        search={<SearchField />}
        secondaryActions={
          <Actions>
            {viewMode === VIEW_MODE_TILES ? (
              <Action custom={<OrderControl />} />
            ) : null}
            <Action
              icon={<DownloadIcon />}
              label={'Download'}
              tooltip={'Download'}
            >
              <Action
                label={'Export to excel'}
                onClick={handleExportExcelConcepts}
              />
              <Action
                label={'Download with filtered variants only'}
                onClick={handleExportZipConceptsFiltered}
              />
              <Action
                label={'Download with all variants'}
                onClick={handleExportZipConcepts}
              />
            </Action>
            <Action component={FullScreenToggler} />
          </Actions>
        }
        primaryActions={
          <Actions>
            {isAllowCreateConcept ? (
              <Action
                border={true}
                button={true}
                add={true}
                iconContent={true}
                tooltip={'Add Concept'}
                variant={'outlined'}
                onClick={handleAddConcept}
              />
            ) : null}
          </Actions>
        }
        modeSwitcher={
          <Actions>
            <Action
              custom={
                <SwitchViewMode viewMode={viewMode} setViewMode={setViewMode} />
              }
            />
          </Actions>
        }
      >
        {tabs}
      </ActionBar>
      {childComponent}
    </>
  );
};

export default Toolbar;
